import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, CheckCircle, Download, Mail, Phone, FileText, 
  Loader2, AlertCircle, ChevronDown, ChevronRight, 
  ExternalLink, Car, BookOpen
} from 'lucide-react';
import { formatPhoneNumber } from '../lib/formatters';
import { toast } from 'sonner';
import PortalFooter from '../components/PortalFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Date formatting - MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Currency formatting
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Progress Step Component
const ProgressStep = ({ number, title, status, onClick, isLast }) => {
  const isCompleted = status === 'completed';
  const isCurrent = status === 'current';
  const isPending = status === 'pending';
  
  return (
    <div className="flex items-center">
      <div 
        className={`flex items-center gap-3 ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all
          ${isCompleted ? 'bg-green-500 text-white' : ''}
          ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : ''}
          ${isPending ? 'bg-slate-200 text-slate-500' : ''}
        `}>
          {isCompleted ? <CheckCircle className="h-5 w-5" /> : number}
        </div>
        <span className={`
          font-medium transition-colors hidden sm:block
          ${isCompleted ? 'text-green-600' : ''}
          ${isCurrent ? 'text-blue-600' : ''}
          ${isPending ? 'text-slate-400' : ''}
        `}>
          {title}
        </span>
      </div>
      {!isLast && (
        <div className={`
          w-8 sm:w-12 h-0.5 mx-2 sm:mx-4
          ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}
        `} />
      )}
    </div>
  );
};

export default function PolicyConfirmationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [showNotices, setShowNotices] = useState(false);

  useEffect(() => {
    const fetchPolicy = async () => {
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const statusResponse = await fetch(`${API_URL}/api/policy/status/${sessionId}`);
        const statusData = await statusResponse.json();

        if (statusData.status === 'pending_info') {
          navigate(`/policyholder-info?session_id=${sessionId}`);
          return;
        }

        if (statusData.status === 'payment_pending') {
          navigate(`/success?session_id=${sessionId}`);
          return;
        }

        if (statusData.status === 'issued' && statusData.policy_number) {
          const policyResponse = await fetch(`${API_URL}/api/policy/details/${statusData.policy_number}`);
          if (policyResponse.ok) {
            const policyData = await policyResponse.json();
            setPolicy(policyData);
          } else {
            setError('Policy details not found');
          }
        } else {
          const policyResponse = await fetch(`${API_URL}/api/policy/${sessionId}`);
          if (policyResponse.ok) {
            const policyData = await policyResponse.json();
            setPolicy(policyData);
          } else {
            setError('Policy not found');
          }
        }
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError('Failed to load policy details');
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, [sessionId, navigate]);

  const handleResendEmail = async () => {
    if (!policy?.policy_number) {
      toast.error('Policy information not available');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/policy/${policy.policy_number}/send-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setEmailSent(true);
        toast.success('Confirmation email sent successfully!');
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to send confirmation email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading your policy details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600">{error}</p>
            <Button onClick={() => navigate('/')} className="mt-4">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const vehicleInfo = policy?.vehicle_info || {};
  const policyholder = policy?.policyholder || {};
  const surplusLinesDisclosure = policy?.surplus_lines_disclosure || {};
  const stateDisclosures = policy?.state_disclosures || {};

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <a href="https://losspay.com">
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-8"
            />
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">
        
        {/* ============================================ */}
        {/* HERO SECTION - Above the Fold */}
        {/* ============================================ */}
        <section className="text-center mb-8">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          
          {/* Headline */}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="confirmation-title">
            Your Coverage Is Active
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg text-slate-600 max-w-xl mx-auto mb-8">
            Let's get your account set up so you can access your policy and file claims anytime.
          </p>
          
          {/* PRIMARY CTA - Activate Account */}
          <a 
            href="/policyholder/setup"
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="activate-account-btn"
          >
            Activate Your Account
            <ChevronRight className="h-5 w-5" />
          </a>
        </section>

        {/* ============================================ */}
        {/* GUIDED SETUP - Progress Steps */}
        {/* ============================================ */}
        <section className="mb-10">
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-6 text-center">
                Complete Your Setup
              </h2>
              
              {/* Progress Steps */}
              <div className="flex items-center justify-center flex-wrap gap-y-4">
                <ProgressStep 
                  number={1} 
                  title="Policy Activated" 
                  status="completed" 
                />
                <ProgressStep 
                  number={2} 
                  title="Activate Account" 
                  status="current"
                  onClick={() => window.location.href = '/policyholder/setup'}
                />
                <ProgressStep 
                  number={3} 
                  title="Review Claims Guide" 
                  status="pending"
                  onClick={() => window.open('https://losspay.com/policyholder/dvpclaimsguide', '_blank')}
                />
                <ProgressStep 
                  number={4} 
                  title="Save Documents" 
                  status="pending"
                  isLast
                />
              </div>
              
              {/* Step 2 CTA */}
              <div className="mt-8 text-center">
                <a 
                  href="/policyholder/setup"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  Continue to Account Activation
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ============================================ */}
        {/* POLICY SUMMARY - Condensed */}
        {/* ============================================ */}
        <section className="mb-8">
          <Card className="shadow-sm" data-testid="policy-summary-card">
            <CardContent className="p-0">
              {/* Header */}
              <div className="bg-slate-800 text-white px-6 py-4 rounded-t-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Policy Summary</span>
              </div>
              
              {/* Key Details */}
              <div className="divide-y divide-slate-100">
                <div className="flex justify-between items-center px-6 py-4">
                  <span className="text-slate-600">Policy Number</span>
                  <span className="font-mono font-semibold text-slate-900" data-testid="policy-number">
                    {policy?.policy_number}
                  </span>
                </div>
                <div className="flex justify-between items-center px-6 py-4">
                  <span className="text-slate-600">Coverage Limit</span>
                  <span className="font-semibold text-blue-600">
                    Up to {formatCurrency(policy?.coverage_amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center px-6 py-4">
                  <span className="text-slate-600">Effective Date</span>
                  <span className="font-medium text-slate-900">{formatDate(policy?.effective_date)}</span>
                </div>
                <div className="flex justify-between items-center px-6 py-4">
                  <span className="text-slate-600">Expiration Date</span>
                  <span className="font-medium text-slate-900">{formatDate(policy?.expiration_date)}</span>
                </div>
              </div>
              
              {/* Expandable Full Details */}
              <div className="border-t border-slate-100">
                <button
                  onClick={() => setShowFullDetails(!showFullDetails)}
                  className="w-full px-6 py-4 flex items-center justify-between text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium">View Full Policy Details</span>
                  <ChevronDown className={`h-5 w-5 transition-transform ${showFullDetails ? 'rotate-180' : ''}`} />
                </button>
                
                {showFullDetails && (
                  <div className="divide-y divide-slate-100 bg-slate-50">
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Named Insured</span>
                      <span className="font-medium text-slate-900 text-sm">
                        {policyholder.first_name} {policyholder.last_name}
                      </span>
                    </div>
                    {policyholder.phone && (
                      <div className="flex justify-between items-center px-6 py-3">
                        <span className="text-slate-600 text-sm">Phone</span>
                        <span className="font-medium text-slate-900 text-sm">
                          {formatPhoneNumber(policyholder.phone)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Covered Vehicle</span>
                      <span className="font-medium text-slate-900 text-sm">
                        {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}{vehicleInfo.trim ? ` ${vehicleInfo.trim}` : ''}
                      </span>
                    </div>
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Coverage Level</span>
                      <span className="font-medium text-slate-900 text-sm">{policy?.coverage_level_name}</span>
                    </div>
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Deductible</span>
                      <span className="font-medium text-slate-900 text-sm">
                        {formatCurrency(policy?.deductible_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Amount Paid</span>
                      <span className="font-medium text-green-600 text-sm">
                        {formatCurrency(policy?.amount_paid)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center px-6 py-3">
                      <span className="text-slate-600 text-sm">Issue Date</span>
                      <span className="font-medium text-slate-900 text-sm">{formatDate(policy?.issue_date)}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ============================================ */}
        {/* CLAIMS READINESS CARD */}
        {/* ============================================ */}
        <section className="mb-8">
          <Card className="shadow-sm border-l-4 border-l-amber-400">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Car className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    If Your Vehicle Is in an Accident
                  </h3>
                  <p className="text-slate-600 mb-4">
                    If your vehicle is damaged in an accident and repaired, it may qualify for a diminished value claim. 
                    After repairs are complete, your vehicle's market value may be lower due to its accident history — 
                    even if it's been perfectly restored. Our coverage helps protect you from this loss.
                  </p>
                  <a 
                    href="https://losspay.com/policyholder/dvpclaimsguide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <BookOpen className="h-4 w-4" />
                    View Claims Guide
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ============================================ */}
        {/* DOCUMENTS - Secondary Actions */}
        {/* ============================================ */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="outline"
              data-testid="download-documents-btn"
              onClick={() => window.open(`${API_URL}/api/policy/${policy?.policy_number}/packet`, '_blank')}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download Full Packet
            </Button>
            <Button
              variant="outline"
              data-testid="resend-email-btn"
              onClick={handleResendEmail}
              disabled={sendingEmail}
              className="gap-2"
            >
              {sendingEmail ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : emailSent ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {sendingEmail ? 'Sending...' : emailSent ? 'Email Sent!' : 'Email Confirmation'}
            </Button>
            <Button
              variant="outline"
              data-testid="view-documents-btn"
              onClick={() => navigate(`/documents/${policy?.policy_number}`)}
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              View Document Center
            </Button>
          </div>
        </section>

        {/* ============================================ */}
        {/* IMPORTANT NOTICES - Collapsible */}
        {/* ============================================ */}
        <section className="mb-8">
          <button
            onClick={() => setShowNotices(!showNotices)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <span className="font-medium text-slate-700">Important Notices</span>
            <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${showNotices ? 'rotate-180' : ''}`} />
          </button>
          
          {showNotices && (
            <div className="mt-3 space-y-4">
              {/* Coverage Reminder */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900 mb-1">Coverage Reminder</p>
                    <p className="text-sm text-blue-800">
                      LossPay Diminished Value Protection is designed to help protect against potential resale value loss after a qualifying repair event. It does not cover vehicle repairs, liability, physical damage, or total loss.
                    </p>
                  </div>
                </div>
              </div>

              {/* Surplus Lines Notice */}
              {surplusLinesDisclosure.requires_disclosure && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900 mb-1">{surplusLinesDisclosure.state_name} Surplus Lines Notice</p>
                      <p className="text-sm text-amber-800">
                        {surplusLinesDisclosure.disclosure_text}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Generic Surplus Lines Notice */}
              {!surplusLinesDisclosure.requires_disclosure && stateDisclosures.requires_surplus_lines_notice && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900 mb-1">Surplus Lines Notice</p>
                      <p className="text-sm text-amber-800">
                        {stateDisclosures.email_disclaimer_block || 'Insurance products offered through LossPay Insurance Agency may be underwritten by surplus lines insurers. Surplus lines insurers are not protected by state insurance guaranty funds.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Compliance Text */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600 leading-relaxed mb-2">
                  LossPay Risk Technologies, Inc., doing business as LossPay Insurance Agency and LossPay, operates as a licensed insurance agency and managing general agency arranging coverage with duly licensed insurers.
                </p>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">
                  All descriptions of coverage on this page are for general informational purposes only. Coverage is subject to the terms, conditions, limits, deductibles, exclusions, eligibility requirements, and state-specific notices contained in the issued policy.
                </p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Insurance products and services are offered only where permitted by law. Availability and eligibility may vary by state.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* ============================================ */}
        {/* CONTACT SUPPORT */}
        {/* ============================================ */}
        <section className="text-center mb-8 pt-6 border-t border-slate-200">
          <p className="text-slate-600 mb-2">Questions? We're here to help.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="tel:8335677729" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <Phone className="h-4 w-4" />
              1-833-LOSSPAY
            </a>
            <span className="text-slate-300">•</span>
            <a href="mailto:support@losspay.com" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <Mail className="h-4 w-4" />
              support@losspay.com
            </a>
          </div>
        </section>

      </main>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}
